import { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import { toUTCFormat, toFormat } from "@/lib/dateHelpers";
import { Path } from "@/router";
import { Person } from "@/types";
import {
  QUALIFYING_LEVEL_REQUIREMENTS_SCROLL_ID,
  membershipProgressCardNavigation,
  type MembershipProgressLinkOptions
} from "./membershipProgressLinks";

type NoProgramMetricOptions = MembershipProgressLinkOptions & {
  canManageJobPosts?: boolean;
};

function activeGroupNames(person: Person): string[] {
  const now = Date.now();
  return (person.groupAssignments ?? [])
    .filter((assignment) => {
      if (!assignment.expiresAt) return true;
      return new Date(assignment.expiresAt).getTime() > now;
    })
    .map((assignment) => assignment.group?.name)
    .filter((name): name is string => !!name);
}

export function buildNoProgramOverviewMetrics(
  person: Person,
  { canManageJobPosts = false, qualifyingLevelSectionEnabled = false, navigate }: NoProgramMetricOptions = {}
): MetricCardType<Path>[] {
  const metrics: MetricCardType<Path>[] = [];

  if (person.accountCreatedAt) {
    metrics.push({
      label: "Account Created",
      value: toUTCFormat(person.accountCreatedAt),
      iconName: "today",
      ...membershipProgressCardNavigation(person.pgaId, {
        qualifyingLevelSectionEnabled,
        scrollTo: QUALIFYING_LEVEL_REQUIREMENTS_SCROLL_ID,
        navigate
      })
    });
  }

  const lead = person.recruitmentLead;
  if (lead?.entryPoint || lead?.referralSource) {
    const hasEntryPoint = !!lead.entryPoint;
    const tooltip =
      hasEntryPoint && lead.referralSource
        ? { title: "Referral Source", supportingText: lead.referralSource }
        : lead.recruitmentDate
          ? { supportingText: `Recruited ${toFormat(lead.recruitmentDate)}` }
          : undefined;

    metrics.push({
      label: hasEntryPoint ? "Entry Point" : "Referral Source",
      value: lead.entryPoint || lead.referralSource || "--",
      iconName: "login",
      ...(tooltip && { tooltip })
    });
  }

  const groups = activeGroupNames(person);
  if (groups.length > 0) {
    metrics.push({
      label: groups.length === 1 ? "Active Group" : "Active Groups",
      value: groups.join(", "),
      iconName: "groups",
      ...(person.pgaId && {
        link: {
          to: "/people/:pgaId/person/groups",
          params: { pgaId: person.pgaId }
        }
      })
    });
  }

  const totalApps = person.totalJobApplications ?? 0;
  const activeApps = person.totalActiveJobApplications ?? 0;
  if (canManageJobPosts && totalApps > 0) {
    metrics.push({
      label: "Job Applications",
      value: activeApps > 0 ? `${activeApps} Active` : `${totalApps} Total`,
      iconName: "work",
      tooltip:
        activeApps > 0 && totalApps !== activeApps ? { supportingText: `${totalApps} total applications` } : undefined,
      ...(person.pgaId && {
        link: {
          to: "/people/:pgaId/career/job-applications",
          params: { pgaId: person.pgaId }
        }
      })
    });
  }

  return metrics;
}
